import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

// Template37: Página de metro Tokio (Consejos para moverse en metro)
// Basado en Subway.jsx de guia-tokio; todo el contenido viene de page (backend).
const Template37 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [metroStickerError, setMetroStickerError] = useState(false);
	const [subwayStickerError, setSubwayStickerError] = useState(false);

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const headerText = page.texts?.[0]?.content;
	const introText = page.texts?.[1]?.content;
	const yamanoteTitle = page.texts?.[2]?.content;
	const yamanoteListHtml = page.texts?.[3]?.content;
	const metroTitle = page.texts?.[4]?.content;
	const metroListHtml = page.texts?.[5]?.content;
	const tarjetasHtml = page.texts?.[6]?.content;
	const appsTitle = page.texts?.[7]?.content;
	const appsListHtml = page.texts?.[8]?.content;
	const consejoHtml = page.texts?.[9]?.content;
	const sabiasQueTitle = page.texts?.[10]?.content;
	const sabiasQueListHtml = page.texts?.[11]?.content;

	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
			? page.data.order
			: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		marginBottom: "12px",
		marginTop: 0,
	};

	const contentColor = textColor || "#444";

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	const metroStickerInBox = getImageSrc("metro-main");
	const subwaySticker = getImageSrc("subway");

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
				"& ul": {
					paddingLeft: "18px",
					margin: 0,
				},
			}}
		>
			{/* CABECERA */}
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 800,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
				</Box>
			)}

			{/* INTRO */}
			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "12px 16px", fontSize: 13, color: contentColor }}>
					<Box component="p" sx={p}>
						{introText}
					</Box>
				</Box>
			)}

			{/* LÍNEAS: JR Yamanote + Líneas de metro */}
			<Box data-block-id="text-2" sx={{ padding: "0 16px", fontSize: 13, color: contentColor }}>
				{yamanoteTitle && (
					<Box component="h3" sx={h3}>
						{yamanoteTitle}
					</Box>
				)}
				{yamanoteListHtml && (
					<Box
						component="div"
						sx={{ "& ul": { paddingLeft: "18px", marginTop: "13px", marginBottom: "13px" }, margin: 0 }}
						dangerouslySetInnerHTML={{ __html: yamanoteListHtml }}
					/>
				)}

				{metroTitle && (
					<Box component="h3" sx={h3}>
						{metroTitle}
					</Box>
				)}
				{metroListHtml && (
					<Box
						component="div"
						sx={{ "& ul": { paddingLeft: "18px", marginTop: "13px", marginBottom: "13px" }, margin: 0 }}
						dangerouslySetInnerHTML={{ __html: metroListHtml }}
					/>
				)}
			</Box>

			{/* TARJETAS */}
			{tarjetasHtml && (
				<Box
					data-block-id="text-3"
					sx={{
						backgroundColor: "#fff8db",
						border: "2px solid #f5c400",
						borderRadius: "12px",
						padding: "10px 12px",
						margin: "16px",
						fontSize: 12,
						color: contentColor,
					}}
					dangerouslySetInnerHTML={{ __html: tarjetasHtml }}
				/>
			)}

			{/* APPS ÚTILES + sticker + consejo */}
			{(appsTitle || appsListHtml || consejoHtml) && (
				<Box
					data-block-id="text-4"
					sx={{
						backgroundColor: "rgb(255, 227, 204)",
						border: "3px solid rgb(242, 139, 53)",
						borderRadius: "16px",
						padding: "16px",
						margin: "0 16px 16px 16px",
						position: "relative",
						width: isSmallScreen ? "60%" : "100%",
					}}
				>
					{appsTitle && (
						<Box
							component="h3"
							sx={{ ...h3, color: "rgb(213, 124, 49)", marginBottom: "8px" }}
						>
							{appsTitle}
						</Box>
					)}
					{appsListHtml && (
						<Box
							component="div"
							sx={{
								paddingLeft: 0,
								margin: 0,
								fontSize: 13,
								"& ul": { paddingLeft: "18px", marginTop: "13px", marginBottom: "13px" },
							}}
							dangerouslySetInnerHTML={{ __html: appsListHtml }}
						/>
					)}
					{metroStickerInBox && !metroStickerError && (
						<Box
							component="img"
							src={metroStickerInBox}
							alt=""
							onError={() => setMetroStickerError(true)}
							sx={{
								width: 110,
								position: "absolute",
								bottom: isSmallScreen ? 0 : -47,
								right: isSmallScreen ? -140 : 12,
							}}
						/>
					)}
					{consejoHtml && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? 90 : 30,
								right: isSmallScreen ? -110 : 30,
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 0px 16px",
								padding: "10px 12px",
								fontSize: 11,
								color: contentColor,
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: 120,
							}}
							dangerouslySetInnerHTML={{ __html: consejoHtml }}
						/>
					)}
				</Box>
			)}

			{/* SABÍAS QUE */}
			{(sabiasQueTitle || sabiasQueListHtml) && (
				<Box sx={{ padding: "0 16px", fontSize: 13, color: contentColor }}>
					{sabiasQueTitle && (
						<Box component="h3" sx={h3}>
							{sabiasQueTitle}
						</Box>
					)}
					{sabiasQueListHtml && (
						<Box
							component="div"
							sx={{ "& ul": { paddingLeft: "18px", marginTop: "13px", marginBottom: "13px" }, margin: 0 }}
							dangerouslySetInnerHTML={{ __html: sabiasQueListHtml }}
						/>
					)}
				</Box>
			)}

			{subwaySticker && !subwayStickerError && (
				<Box
					component="img"
					src={subwaySticker}
					alt=""
					onError={() => setSubwayStickerError(true)}
					sx={{
						width: 100,
						position: "absolute",
						bottom: isSmallScreen ? -90 : -120,
						right: 20,
					}}
				/>
			)}
		</Box>
	);
};

Template37.thumbnail = "/images/template-1.png";

export default Template37;
